/**
 * The game registry. Single source of truth for the menu, the migration order, and
 * which Socket.IO namespaces the API mounts.
 *
 * A game stays reachable through `legacyUrl` on the old Apache vhost until its slice
 * lands, so the new hub can ship before any game is ported.
 */

export type GameStatus =
  /** Still served by the legacy Apache vhost. Menu links off-site. */
  | "legacy"
  /** Being ported right now; hidden from the menu unless explicitly enabled. */
  | "wip"
  /** Served from this codebase. */
  | "live";

export type GameGroup = "casino" | "cards" | "party" | "tool";

export interface GameDefinition {
  id: string;
  /** Polish display name, as it appears in the menu. */
  label: string;
  icon: string;
  /** Path on the current production site. */
  legacyUrl: string;
  /** Route in the new web app, once migrated. */
  route: string;
  status: GameStatus;
  group: GameGroup;
  /**
   * Migration slice from the rework plan. Undefined means "not scheduled yet".
   * Slice 0 is the hub itself.
   */
  slice?: number;
  /**
   * False when the source is referenced by the production Apache config but is not
   * in the repo, so it has to be copied down from the VPS before work can start.
   */
  sourceInRepo: boolean;
  /** Requires a signed-in account (wagering, persistent balance, leaderboards). */
  requiresAuth: boolean;
  /** Multiplayer games need a Socket.IO namespace on the API. */
  realtime: boolean;
}

export const GAMES: readonly GameDefinition[] = [
  {
    id: "pasjans",
    label: "Pasjans",
    icon: "🃏",
    legacyUrl: "/pasjans/",
    route: "/pasjans",
    status: "live",
    group: "cards",
    slice: 1,
    sourceInRepo: true,
    requiresAuth: false,
    realtime: false,
  },
  {
    id: "kolo",
    label: "Koło fortuny",
    icon: "🎡",
    legacyUrl: "/cwel/",
    route: "/kolo",
    status: "live",
    group: "tool",
    slice: 2,
    sourceInRepo: true,
    requiresAuth: false,
    realtime: false,
  },
  {
    id: "kebab",
    label: "Kebab tracker",
    icon: "🥙",
    legacyUrl: "/kebab/",
    route: "/kebab",
    status: "live",
    group: "tool",
    slice: 3,
    sourceInRepo: true,
    requiresAuth: false,
    realtime: false,
  },
  {
    id: "blackjack",
    label: "Blackjack",
    icon: "♠️",
    legacyUrl: "/blackjack/",
    route: "/blackjack",
    status: "live",
    group: "casino",
    slice: 4,
    sourceInRepo: true,
    requiresAuth: true,
    realtime: true,
  },
  {
    id: "ruletka",
    label: "Ruletka",
    icon: "🎯",
    legacyUrl: "/ruletka/",
    route: "/ruletka",
    status: "live",
    group: "casino",
    slice: 5,
    sourceInRepo: true,
    requiresAuth: true,
    realtime: true,
  },
  {
    id: "poker",
    label: "Poker",
    icon: "🂡",
    legacyUrl: "/poker/",
    route: "/poker",
    status: "live",
    group: "casino",
    slice: 6,
    sourceInRepo: true,
    requiresAuth: true,
    realtime: true,
  },
  {
    id: "konie",
    label: "Konie",
    icon: "🐎",
    legacyUrl: "/konie/",
    route: "/konie",
    status: "live",
    group: "casino",
    slice: 7,
    sourceInRepo: true,
    requiresAuth: true,
    realtime: true,
  },
  {
    id: "pan",
    label: "Pan",
    icon: "🂮",
    legacyUrl: "/chuj/",
    route: "/pan",
    status: "legacy",
    group: "cards",
    slice: 8,
    sourceInRepo: false,
    requiresAuth: true,
    realtime: true,
  },
  {
    id: "wordle",
    label: "Polskie Wordle",
    icon: "🟩",
    legacyUrl: "/wordle/",
    route: "/wordle",
    status: "live",
    group: "party",
    sourceInRepo: true,
    requiresAuth: false,
    realtime: false,
  },
  {
    id: "sudoku",
    label: "Sudoku",
    icon: "🧩",
    legacyUrl: "/sudoku/",
    route: "/sudoku",
    status: "live",
    group: "party",
    sourceInRepo: true,
    requiresAuth: false,
    realtime: false,
  },
  {
    id: "2048",
    label: "2048",
    icon: "🔢",
    legacyUrl: "/2048/",
    route: "/2048",
    status: "live",
    group: "party",
    sourceInRepo: true,
    requiresAuth: false,
    realtime: false,
  },

  // Not in the current migration scope. Listed so the menu stays complete and links
  // keep working; give them a slice number when you decide to port them.
  {
    id: "karty",
    label: "Karty",
    icon: "🃎",
    legacyUrl: "/karty/",
    route: "/karty",
    status: "live",
    group: "cards",
    slice: 9,
    sourceInRepo: true,
    requiresAuth: true,
    realtime: true,
  },
  {
    id: "maszyny",
    label: "Maszyny",
    icon: "🎰",
    legacyUrl: "/maszyny/",
    route: "/maszyny",
    status: "live",
    group: "casino",
    slice: 10,
    sourceInRepo: true,
    requiresAuth: true,
    realtime: false,
  },
  {
    id: "8ball",
    label: "8ball",
    icon: "🎱",
    legacyUrl: "/8ball/",
    route: "/8ball",
    status: "live",
    group: "party",
    slice: 11,
    sourceInRepo: true,
    requiresAuth: true,
    realtime: true,
  },
  {
    id: "bottle-clicker",
    label: "Bottle clicker",
    icon: "🍾",
    legacyUrl: "/bottle-clicker/",
    route: "/bottle-clicker",
    status: "live",
    group: "party",
    sourceInRepo: true,
    requiresAuth: false,
    realtime: false,
  },
  {
    id: "kalambury",
    label: "Kalambury",
    icon: "✍️",
    legacyUrl: "/kalambury/",
    route: "/kalambury",
    status: "legacy",
    group: "party",
    sourceInRepo: true,
    requiresAuth: false,
    realtime: true,
  },
];

/** Menu after cutover: unported games are not linked to the old Apache paths. */
export const LIVE_GAMES: readonly GameDefinition[] = GAMES.filter(
  (game) => game.status === "live",
);

export const GAME_GROUP_LABELS: Record<GameGroup, string> = {
  casino: "Kasyno",
  cards: "Karciane",
  party: "Imprezowe",
  tool: "Narzędzia",
};

export function getGame(id: string): GameDefinition | undefined {
  return GAMES.find((game) => game.id === id);
}

/**
 * Where the menu should point for a game: the new route once it is live, the old
 * Apache path until then.
 */
export function resolveGameHref(game: GameDefinition): string {
  return game.status === "live" ? game.route : game.legacyUrl;
}

export function gamesByGroup(group: GameGroup): GameDefinition[] {
  return GAMES.filter((game) => game.group === group);
}

/** Socket.IO namespaces the API needs to mount, derived from the registry. */
export function realtimeNamespaces(): string[] {
  return GAMES.filter((game) => game.realtime && game.status === "live").map(
    (game) => `/${game.id}`,
  );
}
